import { WARFISH_CLASSICS_LONG_FORM } from '../components/map/warfish/presentation'
import { getBundledMap } from './bundledMaps'
import { isNewMap } from './newMap'
import type { MapSummary } from '@warcarp/api-types'

export interface MapPickerOption {
  id: string
  name: string
  /** True for boards with their own special rules (e.g. Capture the Flag, Hexcore). */
  specialRules: boolean
  /** Shipped within the last 30 days (MapDef.addedOn): shown with a NEW tag. */
  isNew: boolean
  legacy?: 'warfish'
}

/**
 * Prefer the server's presentation metadata so maps added after a client build
 * keep their grouping and NEW tag. Bundled definitions cover cold starts and
 * older API responses without metadata.
 * Shared by the new-game and edit-game screens and the maps gallery (which keeps
 * each summary's other fields).
 */
export function mapPickerOptions<T extends Pick<MapSummary, 'id' | 'name' | 'legacy' | 'addedOn' | 'specialRules'>>(summaries: ReadonlyArray<T> | undefined): Array<T & MapPickerOption> {
  return (summaries ?? []).map((summary) => {
    const map = getBundledMap(summary.id)
    const legacy = summary.legacy ?? map?.legacy
    return { ...summary, specialRules: summary.specialRules ?? !!map?.specialRules?.length,
      isNew: isNewMap(summary.addedOn ?? map?.addedOn), ...(legacy ? { legacy } : {}) }
  })
}

/** Shared gallery/picker groups; legacy boards have their own section even with special rules. */
export function mapPickerSections<T extends { id: string; name: string; specialRules?: boolean; legacy?: 'warfish' }>(maps: readonly T[]): { key: 'standard' | 'special' | 'warfish'; title: string | null; maps: T[] }[] {
  return [
    { key: 'standard' as const, title: null, maps: maps.filter((m) => !m.specialRules && m.legacy !== 'warfish') },
    { key: 'special' as const, title: 'Special rules', maps: maps.filter((m) => m.specialRules && m.legacy !== 'warfish') },
    { key: 'warfish' as const, title: WARFISH_CLASSICS_LONG_FORM, maps: maps.filter((m) => m.legacy === 'warfish') },
  ].filter((section) => section.maps.length > 0)
}
